import React, {useEffect, useMemo, useState} from 'react';
import {
    ActionIcon,
    Card,
    Container,
    Grid,
    Group,
    NumberFormatter,
    rem,
    Stack,
    Text,
    TextInput,
    Title
} from "@mantine/core";
import {
    canApproveFinanceLead,
    canApproveTransactionLead, canApproveInvestmentAnalyst,
    canEdit,
    createFormDataObject,
    errorBoundaryWrapper,
    formatDollarAmount, isIECApprovalRequired, priorityToHealthData,
    useAgMutation, useCurrentUser
} from '@repo/utils';
import {
    TransactionTeam,
    useAssetTypesQuery, useChecklistMilestonesQuery,
    useEditTransactionTitleMutation,
    useManagementTypesQuery
} from "@repo/services";
import TransactionTeamApproverCard from "./TransactionTeamApproverCard";
import {financeQuery, landQuery, productionQuery, useAgLazyQuery} from "@repo/utils";
import TransactionTeamExecCard from "./TransactionTeamExecCard";
import RaiseLowerPriorityInput from "../../RaiseLowerPriorityInput";
import {
    ModuleIdentifier,
    useCountriesQuery, useDivisionsQuery, useLazyStateProvincesByCountryQuery,
    useSaleTypesQuery,
    useTransactionTypesQuery
} from "@repo/services";
import {notifications} from "@mantine/notifications";
import {IconSend, IconEdit, IconCircleX} from "@tabler/icons-react";
import './TransactionTeam.css';
import dayjs from "dayjs";
import DetailsItem from './DetailsItem';
import PriorityToHealthLabel from '../../PriorityToHealthLabel';
import { useThemeContext } from '../../theme/ThemeContext';
import { isHighValueTransaction } from '../../../../../apps/aim/src/utils/prismTransactionUtils';

/**
 * Displays the full detail view for a transaction team, including editable title, health/priority, asset and management type, location, dates, contact info, transaction team member approver cards, and (for high-value deals) executive approver cards.
 *
 * @param selectedTransactionTeam - The transaction team record whose details are displayed.
 */
const TransactionTeamDetails = ({ selectedTransactionTeam }: { selectedTransactionTeam: TransactionTeam | undefined }) => {
    const { data: countries = [] } = useCountriesQuery(null);
    const { data: typesOfSale = [] } = useSaleTypesQuery(null);
    const { data: transactionTypes = [] } = useTransactionTypesQuery(null);
    const { data: assetTypes = [] } = useAssetTypesQuery(null);
    const { data: managementTypes = [] } = useManagementTypesQuery(null);
    const { data: checklistMilestones = [] } = useChecklistMilestonesQuery(null);
    const { theme } = useThemeContext();
  // const { data: divisions = [] } = useDivisionsQuery(null);
    const [stateProvinces] = useAgLazyQuery(
        selectedTransactionTeam?.country,
        useLazyStateProvincesByCountryQuery
    );
    const { trigger: triggerEditTransactionTitle, isLoading: editTransactionTitleIsLoading } =
        useAgMutation<TransactionTeam>(useEditTransactionTitleMutation, () => {
            notifications.show({
                title: 'Title Change Successful',
                message: `You successfully changed the title for this transaction!`
            });
            setTitleIsEdit(false);
    });
    const { userRoles } = useCurrentUser();
    const [title, setTitle] = useState<string | undefined>(selectedTransactionTeam?.title)
    const [titleIsEdit, setTitleIsEdit] = useState<boolean>(false)

    const editTitle = () => {
        if (selectedTransactionTeam) {
            const newTitle = title;
            const newTitleFormDataObject = createFormDataObject({ title: newTitle });
            triggerEditTransactionTitle({ aimTransactionTeam: selectedTransactionTeam?.aimTransactionTeam, title: newTitleFormDataObject });
        }
    };

    const handleCanEdit = (isApproval = false) => {
        let teamId: string | undefined = undefined;
        if (selectedTransactionTeam?.aimTransactionTeam?.toString()) teamId = selectedTransactionTeam?.aimTransactionTeam?.toString();
        let teamStatus: string | undefined = undefined;
        if (selectedTransactionTeam?.teamStatus) teamStatus = selectedTransactionTeam?.teamStatus;
        return canEdit(teamId as any, teamStatus as any, isApproval);
    };

    const priorityToHealth = useMemo(() => {
        const priority = selectedTransactionTeam?.priority
        return priorityToHealthData.find((d) => d.value === priority);
    },[selectedTransactionTeam?.priority]);

    const canEditTitle = useMemo(() => {
        const milestone = checklistMilestones?.find((m) => m.aimChecklistMilestone === selectedTransactionTeam?.aimChecklistMilestone);
        if (milestone) {
            const canEdit = milestone.modifyTitle === true;
            if (canEdit) return true;
        }
        return false;
    }, [selectedTransactionTeam, checklistMilestones, handleCanEdit]);

    return (
        <Container pl={0} pr={10} style={{ height: '100%', width: '100%' }} size="responsive">
            <Stack pt={20} pb={120}>
                <Card shadow='sm' padding='lg' radius='md' withBorder>
                    <Grid>
                        <Grid.Col span={{ sm: 6, md: 3 }}>
                            <DetailsItem
                              title={'transaction name'}
                              text={
                                <>
                                    {canEditTitle && (
                                      <>
                                          <Group gap={0} w={'100%'}>
                                              <TextInput
                                                w={'75%'}
                                                disabled={!titleIsEdit}
                                                defaultValue={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                className="title-input"
                                              />
                                              <ActionIcon.Group w={'25%'}>
                                                  {titleIsEdit && (
                                                    <>
                                                        <ActionIcon
                                                          disabled={!canEditTitle}
                                                          loading={editTransactionTitleIsLoading}
                                                          onClick={() => setTitleIsEdit(false)}
                                                          variant="default"
                                                          size="lg"
                                                          h={36}
                                                          w={50}
                                                          aria-label="Close"
                                                          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                                                        >
                                                            <IconCircleX style={{ width: rem(20) }} stroke={1.5} />
                                                        </ActionIcon>
                                                        <ActionIcon
                                                          disabled={!canEditTitle}
                                                          loading={editTransactionTitleIsLoading}
                                                          onClick={editTitle}
                                                          color="green"
                                                          size="lg"
                                                          h={36}
                                                          w={50}
                                                          aria-label="Submit New Title"
                                                        >
                                                            <IconSend style={{ width: rem(20) }} stroke={1.5} />
                                                        </ActionIcon>
                                                    </>
                                                  )}
                                                  {!titleIsEdit && (
                                                    <ActionIcon
                                                      disabled={!canEditTitle}
                                                      loading={editTransactionTitleIsLoading}
                                                      onClick={() => setTitleIsEdit(true)}
                                                      size="lg"
                                                      color="blue"
                                                      h={36}
                                                      w={50}
                                                      aria-label="Edit Transaction Title"
                                                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                                                    >
                                                        <IconEdit style={{ width: rem(20) }} stroke={1.5} />
                                                    </ActionIcon>
                                                  )}
                                              </ActionIcon.Group>
                                          </Group>
                                      </>
                                    )}
                                    {!canEditTitle && (
                                      <Text>{selectedTransactionTeam?.title}</Text>
                                    )}
                                </>
                              }
                            />
                        </Grid.Col>
                        <Grid.Col span={{ sm: 6, md: 3 }}>
                            <DetailsItem
                              title={'health'}
                              text={
                                <>
                                    {/*{!canChangeTxTeamPriority(userRoles) && (*/}
                                    {/*  <PriorityToHealthLabel label={priorityToHealth?.label || ""} color={priorityToHealth?.color || "gray"} />*/}
                                    {/*)}*/}
                                    {/*{canChangeTxTeamPriority(userRoles) && (*/}
                                      <RaiseLowerPriorityInput
                                        priority={selectedTransactionTeam?.priority}
                                        module={ModuleIdentifier.aimTransactionTeam}
                                        id={selectedTransactionTeam?.aimTransactionTeam}
                                        status={selectedTransactionTeam?.aimChecklistMilestone}
                                      />
                                    {/*)}*/}
                                </>
                            }
                            />
                        </Grid.Col>
                        <Grid.Col span={{ sm: 12, md: 3 }}>
                            <DetailsItem title={'asset type'} text={assetTypes?.find((d) => d.aimAssetType === selectedTransactionTeam?.aimAssetType)?.description || selectedTransactionTeam?.aimAssetType} />
                        </Grid.Col>
                        <Grid.Col span={{ sm: 12, md: 3 }}>
                            <DetailsItem title={'management type'} text={managementTypes?.find((d) => d.aimManagementType === selectedTransactionTeam?.aimManagementType)?.description || selectedTransactionTeam?.aimManagementType} />
                        </Grid.Col>
                    </Grid>
                </Card>
                <Card shadow='sm' padding='lg' radius='md' withBorder>
                    <Grid>
                        <Grid.Col span={4}>
                            <DetailsItem title={'state/province'} text={stateProvinces?.find((sp) => sp.stateProvince === selectedTransactionTeam?.stateProvince)?.name || selectedTransactionTeam?.stateProvince} />
                        </Grid.Col>
                        <Grid.Col span={4}>
                            <DetailsItem title={'county'} text={selectedTransactionTeam?.county} />
                        </Grid.Col>
                        <Grid.Col span={4}>
                            <DetailsItem title={'country'} text={countries?.find((c) => c.country === selectedTransactionTeam?.country)?.name || selectedTransactionTeam?.country} />
                        </Grid.Col>
                    </Grid>
                </Card>
                <Card shadow='sm' padding='lg' radius='md' withBorder>
                    <Stack>
                        <Grid>
                            <Grid.Col span={3}>
                                <DetailsItem title={'estimated acres'} text={<NumberFormatter value={selectedTransactionTeam?.estAcres} decimalScale={2} thousandSeparator />} />
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <DetailsItem title={'offer/bid date'} text={selectedTransactionTeam?.bidDate
                                  ? dayjs(selectedTransactionTeam?.bidDate).format('MM-DD-YYYY')
                                  : ""} />
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <DetailsItem title={'estimated completion date'} text={selectedTransactionTeam?.estCompletion
                                  ? dayjs(selectedTransactionTeam?.estCompletion).format('MM-DD-YYYY')
                                  : ""} />
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <DetailsItem
                                  title={'estimated price USD'}
                                  text={
                                    <Group>
                                        <NumberFormatter
                                          value={selectedTransactionTeam?.estPrice || 0} prefix={'$'}
                                          thousandSeparator decimalScale={0}
                                        />
                                    </Group>
                                  }
                                />
                            </Grid.Col>
                        </Grid>
                        <Grid>
                            {/*<Grid.Col span={4}>*/}
                            {/*    <DetailsItem title={'iec approval required'} text={isIECApprovalRequired(selectedTransactionTeam) ? "Yes" : "No"} />*/}
                            {/*</Grid.Col>*/}
                            <Grid.Col span={4}>
                                <DetailsItem title={'sale type'} text={typesOfSale?.find((t) => t?.aimSaleType === selectedTransactionTeam?.aimSaleType)?.description || selectedTransactionTeam?.aimSaleType} />
                            </Grid.Col>
                            <Grid.Col span={4}>
                                <DetailsItem title={'transaction type'} text={transactionTypes?.find((t) => t.aimTransactionType === selectedTransactionTeam?.aimTransactionType)?.description || selectedTransactionTeam?.aimTransactionType} />
                            </Grid.Col>
                        </Grid>
                    </Stack>
                </Card>
              <Card shadow='sm' padding='lg' radius='md' withBorder>
                    <Stack>
                        <Grid>
                            <Grid.Col span={4}>
                                <DetailsItem title={'internal contact name'} text={selectedTransactionTeam?.internalContactName} />
                            </Grid.Col>
                            <Grid.Col span={4}>
                                <DetailsItem title={'external contact name'} text={selectedTransactionTeam?.contactName} />
                            </Grid.Col>
                            <Grid.Col span={4}>
                                <DetailsItem title={'external contact email'} text={selectedTransactionTeam?.contactEmail} />
                            </Grid.Col>
                        </Grid>
                        <Grid>
                            <Grid.Col span={4}>
                                <DetailsItem title={'external contact phone'} text={selectedTransactionTeam?.contactPhone} />
                            </Grid.Col>
                            <Grid.Col span={8}>
                                <DetailsItem title={'description'} text={selectedTransactionTeam?.description} />
                            </Grid.Col>
                        </Grid>
                    </Stack>
                </Card>
                <Card shadow='sm' padding='lg' radius='md' withBorder>
                    <DetailsItem
                      title={'transaction team members'}
                      text={
                          <Grid>
                              <Grid.Col span={{ lg: 6, md: 12 }}>
                                  <TransactionTeamApproverCard
                                    filters={landQuery}
                                    employeeId={selectedTransactionTeam?.landLead}
                                    label={"Transaction Lead"}
                                    role={"land"}
                                    txId={selectedTransactionTeam?.aimTransactionTeam}
                                    txTeamName={selectedTransactionTeam?.title}
                                    canUpdate={selectedTransactionTeam?.landApproved || false}
                                    approved={selectedTransactionTeam?.landApproved || false}
                                    approvedDate={selectedTransactionTeam?.landApprovedAt || ""}
                                    hasApproverRole={canApproveTransactionLead(userRoles)}
                                  />
                              </Grid.Col>
                              <Grid.Col span={{ lg: 6, md: 12 }}>
                                  <TransactionTeamApproverCard
                                    filters={productionQuery}
                                    employeeId={selectedTransactionTeam?.operationsLead}
                                    label={"Investment Analyst"}
                                    role={"operations"}
                                    txId={selectedTransactionTeam?.aimTransactionTeam}
                                    txTeamName={selectedTransactionTeam?.title}
                                    canUpdate={selectedTransactionTeam?.operationsApproved || false}
                                    approved={selectedTransactionTeam?.operationsApproved || false}
                                    approvedDate={selectedTransactionTeam?.operationsApprovedAt || ""}
                                    hasApproverRole={canApproveInvestmentAnalyst(userRoles)}
                                  />
                              </Grid.Col>
                              <Grid.Col span={{ lg: 6, md: 12 }}>
                                  <TransactionTeamApproverCard
                                    filters={financeQuery}
                                    employeeId={selectedTransactionTeam?.financeLead}
                                    label={"Financial Analyst"}
                                    role={"finance"}
                                    txId={selectedTransactionTeam?.aimTransactionTeam}
                                    txTeamName={selectedTransactionTeam?.title}
                                    canUpdate={selectedTransactionTeam?.financeApproved || false}
                                    approved={selectedTransactionTeam?.financeApproved || false}
                                    approvedDate={selectedTransactionTeam?.financeApprovedAt || ""}
                                    hasApproverRole={canApproveFinanceLead(userRoles)}
                                  />
                              </Grid.Col>
                          </Grid>
                      }
                    />
                </Card>
                {selectedTransactionTeam?.highValueDeal && (
                    <Card shadow='sm' padding='lg' radius='md' withBorder>
                        <DetailsItem
                          title={'executive members'}
                          text={
                              <Grid>
                                  <Grid.Col span={{ lg: 6, md: 12 }}>
                                      <TransactionTeamExecCard role={"ceo"} />
                                  </Grid.Col>
                                  <Grid.Col span={{ lg: 6, md: 12 }}>
                                      <TransactionTeamExecCard role={"cfo"} />
                                  </Grid.Col>
                              </Grid>
                          }
                        />
                    </Card>
                )}
            </Stack>
        </Container>
    );
};

export default errorBoundaryWrapper(TransactionTeamDetails);
